import React, { useCallback, useEffect, useState } from 'react'
import { type GridColDef, type GridSortModel } from '@mui/x-data-grid-pro'
import WrappedDataGrid from '../../components/data-grid/wrapped-data-grid/wrapped-data-grid'
import { type MODE_OPTIONS } from '../../components/data-grid/wrapped-data-grid/wrapped-data-grid-types'
import { type ExposureDataGridRow } from './exposure-data-grid'
import {
  type CoinExposureData, type AccountExposureData,
  type PortfolioExposureData, type SymbolExposureData
} from '../../models/exposure/exposure-types'
import {
  createCoinExposureRows, extractForHeaderInitialExposure as extractInitialExposureByCoin, prefilterCoinExposureData,
} from '../../utils/view-specific/exposure/coin-exposure-utils'
import {
  createAccountExposureRows, extractForHeaderInitialExposure as extractInitialExposureByAccount
} from '../../utils/view-specific/exposure/account-exposure-utils'
import { createSymbolExposureRows } from '../../utils/view-specific/exposure/symbol-exposure-utils'
import { EXPOSURE_MODE_OPTIONS_CONFIG } from './exposure-data-grid-col-definitions'

interface ExposureDataGridStaticProps {
  portfolio: string
  dataMode: MODE_OPTIONS
  data: PortfolioExposureData | null
}

const ExposureDataGridStatic: React.FC<ExposureDataGridStaticProps> = ({ portfolio, dataMode, data }) => {
  const [mode, setMode] = useState<MODE_OPTIONS>(dataMode)

  const [stableCoinVisibility, setStableCoinVisibility] = useState<boolean>(false)
  const toggleStableCoinVisibility = useCallback(() => {
    setStableCoinVisibility(!stableCoinVisibility)
  }, [stableCoinVisibility])

  const modeConfig = EXPOSURE_MODE_OPTIONS_CONFIG[mode]
  const modeColumns: GridColDef[] = modeConfig.columns
  const modeAutosizeOptions = modeConfig.autosizeOptions
  const modeSortModel = modeConfig.sortModel

  const [rows, setRows] = useState<ExposureDataGridRow[]>([])
  const [timestamp, setTimestamp] = useState<number>(0)
  const [totalCurrAsset, setTotalCurrAsset] = useState<string>('')
  const [pnlAsset, setPnLAsset] = useState<string>('')
  const [nextFund, setNextFund] = useState<string>('')
  const [initialExposureByCoin, setInitialExposureByCoin] = useState<Record<string, string>>({})
  const [sortModel, setSortModel] = React.useState<GridSortModel>(modeSortModel)

  useEffect(() => {
    if (data == null) {
      setTimestamp(0) // Only for ExposureDataGridStatic
      setRows([])
      setTotalCurrAsset('')
      setPnLAsset('')
      setNextFund('')
      setInitialExposureByCoin({})
    } else {
      const portfolioData = data[portfolio]
      switch (mode) {
        case 'coin':
          setRows(stableCoinVisibility
            ? createCoinExposureRows(portfolioData as CoinExposureData)
            : createCoinExposureRows(prefilterCoinExposureData(portfolioData as CoinExposureData))
          )
          setInitialExposureByCoin(extractInitialExposureByCoin(portfolioData as CoinExposureData))
          break
        case 'account':
          setRows(createAccountExposureRows(portfolioData as AccountExposureData))
          setInitialExposureByCoin(extractInitialExposureByAccount(portfolioData as AccountExposureData))
          break
        case 'symbol':
          setRows(createSymbolExposureRows(portfolioData as SymbolExposureData))
          setInitialExposureByCoin({})
          break
      }

      setTimestamp(data.timestamp)
      setTotalCurrAsset(portfolioData.total_curr_asset)
      setPnLAsset(portfolioData.pnl_asset)
      setNextFund(portfolioData.next_fund)

      setSortModel(modeSortModel)
    }
  }, [mode, stableCoinVisibility, data])

  return (
    <WrappedDataGrid
      gridData={{
        rows,
        columns: modeColumns,
        autosizeOptions: modeAutosizeOptions,
        sortModel,
        portfolio,
        csvOptions: { fileName: portfolio + '_exposure' }
      }}
      gridMode={{
        mode,
        modeOptions: [dataMode],
        setMode,
      }}
      stableCoinVisibility={{
        stableCoinVisibility,
        toggleStableCoinVisibility
      }}
      toolbarData={{
        timestamp,
        pnlAsset,
        nextFund,
        totalCurrAsset,
        initialExposureByCoin,
      }}
      styleData={{
        background: 'rgba(70, 43, 0, 0.25)',
      }}
    />
  )
}

export default ExposureDataGridStatic
